/**
 * 見積依頼書の作成画面 /rfqs/:id（#119 docs/56 → #218 docs/88 §4.2 → #220 Figma ②改2 node 206-74・207-86）。見積書の作成画面と同じ形（入口は3つ・画面は1つ）
 *   頭の帯（2段）：1段目＝「‹」・題「見積依頼書」・No.（下書きは札「下書き」だけ＝2026-10-21 ★17）・状態チップ・{project}名（小）・自動保存 14:02／右に ↶ ↷・主なボタン1つ（下書き＝送付・送ったあと＝回答を登録）＋「⋯」（ボタンは 40px）
 *           2段目＝{vendor}・件名・依頼日・回答期限（？）・担当者・詳細 ⌄（{site}・{period}・見積期間の知らせ）、右端に紐づく見積「見積 Q-0005 ›」。パンくずの行は無い
 *   添付の帯（1行 52px）：「添付」件数・「2.0 ／ 10 MB」・ファイルのチップ（種類・名前・×）・…・「送信設定　メール・写真台帳・Bcc ⌄」の要約・「書類から選択」「ファイル追加」（36px）
 *     要約を押すと帯の下に送信設定が1段広がる（既定は閉じた形・開閉は人ごとに覚える）＝送信先／送信方法／添付（写真台帳・{takeoff}・控え Bcc）／印字項目（回答記入欄・条件欄）／次回も同じ設定・閉じる
 *   明細のカード：道具の行（見積明細から取込・行を追加・見出しを追加。36px）・明細の表（EstimateTable の外枠＋RFQ_COLS。行 44px）・いちばん下に条件・依頼文の1行（押すと広がる）
 *     単価・金額は「回答欄」（打てない）。社内用の列・合計は無い。元の列＝「見積 行 n」（見積から写した行）／「追加」（手で足した行）
 *   「送付」は短い確認だけ（SendDialog）。設定の変更は帯で行う。2026-10-21 射場さんの決め（docs/89 ★16）：印刷・FAX と、メールで送付状がオンのときは
 *   送付の画面 /rfqs/:id/send（送付状の枠と紙＝見積書・請求書の紙の画面と同じ形）へ移る。メールで送付状がオフなら今の短い確認の窓
 *   送ったあとは全部見るだけ。送った記録と回答は明細の下
 *   保存は共通の仕組み（lib/saveQueue.tsx）：打つ欄は 0.6 秒後・選ぶ／切り替えはすぐ・送るのは1本ずつ・版つき（409 RFQ_STALE は最新を読んで、この画面で修正した欄を重ねて送り直す）
 *   送付・PDF の前は ready()（保存できていなければ進まない）。離れるときは「続ける／破棄」
 *   規格・備考の幅は人ごとの設定 rfqColumns（#220 で見積書と分けた。無ければ見積書の幅）
 *   スマホ（幅 767px 以下・スマホ版）：見るだけ（帯は縦に積む・送信設定は閉じた形で1行）
 *   売価はどこにも出ない（API の型に無い）
 */
import {
  ORDER_MSG,
  CONFLICT_MSG,
  COVER_LETTER_MSG,
  MSG,
  RECEIVED_ACCEPT,
  RFQ_LIMITS,
  RFQ_MSG,
  RFQ_STATUS_LABEL,
  UndoStack,
  pickNamesOf,
  rfqAttachMeter,
  rfqSizeText,
  saveGateText,
  type PartnerList,
  type ReceivedDocument,
  type ReceivedList,
  type Rfq,
  type RfqEstimateLines,
  type RfqLine,
  type RfqLineInput,
  type RfqUpdate,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMe } from "../../auth.js";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, apiUpload, ApiError, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { DocField, DocHead, subjWidth } from "../../components/DocHead.jsx";
import { FOLD_MSG } from "../../components/FoldBand.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { FileDrop } from "../../components/FileDrop.jsx";
import { DOTS_H, HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { notify } from "../../components/Notice.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { RfqPurgeDialog } from "./PurgeDialog.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { ErrorText, Field, Help, Switch, useToast } from "../../components/ui.jsx";
import { useUses } from "../../core/uses.js";
import { t, useLabels } from "../../labels.js";
import { fq } from "../../lib/num.js";
import { usePdfTab } from "../../lib/pdfOpen.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { useNarrow } from "../../lib/useNarrow.js";
import { EstimateTable, type TblCtx } from "../estimates/EstimateTable.jsx";
import { rfqColumnsFor } from "../estimates/columns.js";
import { useEstimateColumns } from "../estimates/colWidths.js";
import { CreateRfqDialog } from "./GroupRfqs.jsx";
import { md, mdTime, overdueText } from "./parts.jsx";
import { SendDialog } from "./SendDialog.jsx";
import { readSendOpen, readSendPrefs, takeNewRfq, writeSendOpen, writeSendPrefs, type RfqSendPrefs } from "./sendPrefs.js";
import { DeliveriesLink } from "../../components/Deliveries.jsx";

const KEY = (id: string) => ["rfq", id] as const;
const newId = (): string => crypto.randomUUID();
const same = (a: RfqLine[], b: RfqLine[]) => JSON.stringify(a) === JSON.stringify(b);
const inputOf = (l: RfqLine): RfqLineInput => ({ id: l.id, kind: l.kind, name: l.name, spec: l.spec, quantity: l.kind === "detail" ? l.quantity : null, unit: l.kind === "detail" ? l.unit : "", note: l.note, itemId: l.itemId });
const blank = (kind: RfqLine["kind"] = "detail"): RfqLine => ({ id: newId(), kind, source: "own", lineageId: null, estimateNo: null, name: "", spec: "", quantity: null, unit: "", note: "", itemId: null, place: null, trade: null });

type Head = Pick<Rfq, "subject" | "siteAddress" | "workPeriodText" | "replyBy" | "requestDate" | "terms">;
const headOf = (r: Rfq): Head => ({ subject: r.subject, siteAddress: r.siteAddress, workPeriodText: r.workPeriodText, replyBy: r.replyBy, requestDate: r.requestDate, terms: r.terms });
/** 欄の並び（Enter・Tab で次の欄へ） */
const FIELDS = ["name", "spec", "qty", "unit", "note"] as const;
type FieldKey = (typeof FIELDS)[number];

export function RfqPage() {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const L = useLabels();
  const can = useUses();
  const narrow = useNarrow();
  // 規格・備考の幅（見積書と同じ部品。保存は見積依頼書の人ごとの設定 rfqColumns＝#220 で見積書と分けた。無ければ見積書の幅）
  const sizer = useEstimateColumns("rfq");
  const me = useMe();
  const account = me.data?.session.accountId ?? null;
  const { toast } = useToast();
  const q = useQuery({ queryKey: KEY(id), queryFn: () => api<Rfq>("GET", `/rfqs/${id}`) });
  const r = q.data;
  const back = useBackNav(r?.project ? { to: `/groups/${r.project.id}?tab=vendor`, label: r.project.name } : { to: "/rfqs", label: RFQ_MSG.listTitle });
  usePageName(RFQ_MSG.title, r ? docShort(RFQ_MSG.title, r.no) : null);

  const [head, setHead] = useState<Head | null>(null);
  const [lines, setLines] = useState<RfqLine[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const version = useRef("");
  /** 送る前の直し（欄ごと）。断られたら残して、次に送るときに送り直す（#177） */
  const pending = useRef<Omit<RfqUpdate, "version">>({});
  const linesRef = useRef<RfqLine[]>([]);
  const undo = useRef(new UndoStack<RfqLine[]>(same));
  const [, bump] = useState(0);
  const [via, setVia] = useState<"email" | "print">("email");
  const [bcc, setBcc] = useState(true);
  /** 送付状（#223、docs/89 §5.2）：印刷・FAX なら既定オン・メールなら既定オフ。窓付封筒は送付状がオンのときだけ（★8） */
  const [cover, setCover] = useState(false);
  const [coverWindow, setCoverWindow] = useState(false);
  const [open, setOpen] = useState(false);
  /** 送信設定の段（帯の下に1段広がる。既定は閉じた形・人ごとに覚える） */
  const [sendOpen, setSendOpenState] = useState(() => readSendOpen(account));
  /** 「次回も同じ設定」（オン＝送信設定をこの端末に覚え、次に作る見積依頼書にも使う） */
  const [remember, setRemember] = useState(() => readSendPrefs(account) != null);
  /** 条件・依頼文の1行を広げたか */
  const [termsOpen, setTermsOpen] = useState(false);
  const [dlg, setDlg] = useState<null | "send" | "resend" | "cancel" | "delete" | "purge" | "import" | "attach" | "copy">(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [upErr, setUpErr] = useState<string | undefined>();
  const [focus, setFocus] = useState<{ id: string; f: FieldKey } | null>(null);
  const pdfTab = usePdfTab();

  // はじめて読んだとき・別の依頼に移ったとき・送ったあと（固めた値）に入れ直す
  useEffect(() => {
    if (!r) return;
    version.current = r.updatedAt;
    if (loadedId !== r.id || !r.canEdit) {
      setHead(headOf(r));
      setLines(r.lines);
      linesRef.current = r.lines;
      setLoadedId(r.id);
      // 送信方法・控え Bcc は画面の中だけの値：「次回も同じ設定」で覚えた値があればそれで始める（#220）
      const prefs = readSendPrefs(account);
      const v0 = prefs?.via ?? (r.contact?.email ? "email" : "print");
      setVia(v0);
      setBcc(prefs?.bcc ?? true);
      setCover(prefs?.cover ?? v0 === "print");
      setCoverWindow(prefs?.coverWindow ?? false);
      undo.current.clear();
      // 作った直後の下書きにだけ、覚えた 添付・印字項目 を当てる（ほかの人が設定した下書きを上書きしない）
      if (prefs && r.canEdit && takeNewRfq(r.id)) {
        const p: Partial<Pick<Rfq, "printAnswerBox" | "printTerms" | "includePhotos" | "photosPerPage" | "includeTakeoffs">> = {};
        if (r.printAnswerBox !== prefs.printAnswerBox) p.printAnswerBox = prefs.printAnswerBox;
        if (r.printTerms !== prefs.printTerms) p.printTerms = prefs.printTerms;
        if (r.show.photos && r.includePhotos !== prefs.includePhotos) p.includePhotos = prefs.includePhotos;
        if (r.show.photos && r.photosPerPage !== prefs.photosPerPage) p.photosPerPage = prefs.photosPerPage;
        if (r.show.takeoff && r.includeTakeoffs !== prefs.includeTakeoffs) p.includeTakeoffs = prefs.includeTakeoffs;
        if (Object.keys(p).length) {
          qc.setQueryData<Rfq>(KEY(r.id), (old) => (old ? { ...old, ...p } : old));
          pending.current = { ...pending.current, ...p };
          window.setTimeout(() => sv.mark(true), 0);
        }
      }
    }
  }, [r?.id, r?.canEdit, r?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // 一覧の「別の{vendor}へ複製」から来たとき：窓を開く
  useEffect(() => {
    if (sp.get("copy") === "1" && r) {
      setDlg("copy");
      const next = new URLSearchParams(sp);
      next.delete("copy");
      setSp(next, { replace: true });
    }
  }, [r?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAround = () => {
    void qc.invalidateQueries({ queryKey: ["rfqs"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    if (r?.estimate) void qc.invalidateQueries({ queryKey: ["estimate-rfqs", r.estimate.id] });
    if (r?.project) void qc.invalidateQueries({ queryKey: ["received", r.project.id] });
  };
  const put = (v: Rfq) => {
    qc.setQueryData(KEY(v.id), v);
    version.current = v.updatedAt;
  };

  const sv = useSaveQueue({
    wait: 600,
    maxWait: Infinity,
    send: async () => {
      const body = pending.current;
      pending.current = {};
      if (!Object.keys(body).length) return;
      const sentLines = body.lines;
      try {
        let v: Rfq;
        try {
          v = await api<Rfq>("PUT", `/rfqs/${id}`, { ...body, version: version.current });
        } catch (e) {
          if (!(e instanceof ApiError && e.code === "RFQ_STALE")) throw e;
          // ほかで変わった：最新を読み、この画面で修正した欄だけを重ねて送り直す（明細はこの画面の形で上書き）
          const latest = await api<Rfq>("GET", `/rfqs/${id}`);
          version.current = latest.updatedAt;
          v = await api<Rfq>("PUT", `/rfqs/${id}`, { ...body, version: latest.updatedAt });
          notify({ text: CONFLICT_MSG.autoMerged });
        }
        put(v);
        // 送っている間に打った明細が無ければ、サーバーの答え（出どころ・見積の No）で入れ替える
        if (sentLines && !pending.current.lines && JSON.stringify(sentLines) === JSON.stringify(linesRef.current.map(inputOf))) {
          setLines(v.lines);
          linesRef.current = v.lines;
        }
      } catch (e) {
        pending.current = { ...body, ...pending.current };
        throw e;
      }
    },
  });
  const patch = (p: Omit<RfqUpdate, "version">, now = false) => {
    pending.current = { ...pending.current, ...p };
    sv.mark(now);
  };
  /** 明細を変える（変える前を控える＝↶ で戻せる。key が同じあいだは1手にまとめる） */
  const setRows = (next: RfqLine[], key: string | null = null, now = false) => {
    undo.current.record(linesRef.current, key);
    linesRef.current = next;
    setLines(next);
    patch({ lines: next.map(inputOf) }, now);
    bump((x) => x + 1);
  };
  const undoDo = (redo: boolean) => {
    const to = redo ? undo.current.redo(linesRef.current) : undo.current.undo(linesRef.current);
    if (!to) return;
    linesRef.current = to;
    setLines(to);
    patch({ lines: to.map(inputOf) }, true);
    bump((x) => x + 1);
  };
  /** 送付・印刷・PDF の前：書きかけを送り終えるのを待つ。保存できていなければ進まずに理由を出す（#177） */
  const gate = async (action: "send" | "print" | "pdf"): Promise<boolean> => {
    const why = await sv.ready();
    if (why != null) toast(saveGateText(action, why), undefined, "error");
    return why == null;
  };
  /** 送付状の型（送付の画面・PDF で確認に渡す）：fax／window／off */
  const coverParam = (on: boolean) => (on ? (coverWindow ? "window" : "fax") : "off");
  /** 「PDF で確認（透かし入り）」：送付状がオンなら送付状込み（送付状の見本も透かし「下書き」。★16） */
  const openDraftPdf = () => void gate("pdf").then((ok) => ok && nav(`/rfqs/${id}/paper${cover ? `?cover=${coverParam(true)}` : ""}`));
  /**
   * 送付の画面へ（★16）：印刷・FAX はいつも・メールは送付状がオンのとき。書きかけを送り終えてから移る（送付の画面は保存した下書きを読む）。
   * 送信設定（送信方法・送付状・窓付封筒・控え Bcc）は道の問い合わせで渡す
   */
  const toSendPage = (v: "email" | "print", coverOn: boolean) =>
    void gate(v === "email" ? "send" : "print").then((ok) => ok && nav(`/rfqs/${id}/send?via=${v}&cover=${coverParam(coverOn)}${v === "email" ? `&bcc=${bcc ? 1 : 0}` : ""}`));

  const vendors = useQuery({ queryKey: ["partners", "", "active", "vendor"], queryFn: () => api<PartnerList>("GET", "/partners?type=vendor"), enabled: !!r?.canEdit && !narrow });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!r || !head || loadedId !== r.id) return <div className="rqe" aria-busy="true" data-testid="rfq-page" />;
  const draft = r.canEdit && !narrow;
  const setH = (p: Partial<Head>, now = false) => {
    setHead({ ...head, ...p });
    patch(
      {
        ...(p.subject !== undefined ? { subject: p.subject } : {}),
        ...(p.siteAddress !== undefined ? { siteAddress: p.siteAddress } : {}),
        ...(p.workPeriodText !== undefined ? { workPeriodText: p.workPeriodText } : {}),
        ...(p.replyBy !== undefined ? { replyBy: p.replyBy || null } : {}),
        ...(p.requestDate !== undefined ? { requestDate: p.requestDate || null } : {}),
        ...(p.terms !== undefined ? { terms: p.terms } : {}),
      },
      now,
    );
  };
  const pdfUrl = `/api/v1/rfqs/${r.id}/pdf`;
  const counts = { est: lines.filter((l) => l.source === "estimate").length, own: lines.filter((l) => l.source === "own").length };

  const act = async (fn: () => Promise<Rfq | void>, done?: string) => {
    setBusy(true);
    try {
      const v = await fn();
      if (v) {
        put(v);
        if (v.id !== r.id) nav(`/rfqs/${v.id}`);
      }
      refreshAround();
      if (done) toast(done);
      setDlg(null);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      await api<void>("DELETE", `/rfqs/${r.id}`);
      refreshAround();
      toast(RFQ_MSG.deleted);
      nav(r.project ? `/groups/${r.project.id}?tab=vendor` : "/rfqs", { replace: true });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
      setBusy(false);
    }
  };
  /** 添付（受け取った書類の id の並び）を変える＝すぐ保存 */
  const setAttach = async (ids: string[]) => {
    if (!(await sv.settle())) return;
    await act(() => api<Rfq>("PUT", `/rfqs/${r.id}`, { version: version.current, attachmentIds: ids }));
  };
  const upload = async (files: File[]) => {
    if (!r.project) return;
    setUpErr(undefined);
    const ids = r.attachments.map((a) => a.documentId);
    for (const f of files) {
      try {
        const kind = f.type.startsWith("image/") && f.type !== "image/tiff" ? "photo" : "drawing";
        const doc = await apiUpload<ReceivedDocument>(`/groups/${r.project.id}/received-documents`, f, f.type || "application/octet-stream", {
          "x-gb-title": `UTF-8''${encodeURIComponent(f.name)}`,
          "x-gb-source": "upload",
          "x-gb-kind": kind,
        });
        ids.push(doc.id);
      } catch (e) {
        setUpErr(messageOf(e));
      }
    }
    await setAttach(ids);
  };

  const actions: HeadAction[] = narrow
    ? // スマホ（幅 767px 以下・スマホ版）：見るだけ。PDF と回答あり（作る・送るのは PC）
      [
        ...(r.status === "sent" || r.status === "answered"
          ? [{ label: RFQ_MSG.answer, checked: r.status === "answered", testId: "rfq-answer", onSelect: () => void act(() => (r.status === "answered" ? api<Rfq>("DELETE", `/rfqs/${r.id}/answer`) : api<Rfq>("POST", `/rfqs/${r.id}/answer`, {})), r.status === "answered" ? RFQ_MSG.unanswered : RFQ_MSG.answered) }]
          : []),
        r.status === "draft" ? { label: RFQ_MSG.pdfDraft, group: "doc" as const, testId: "rfq-pdf-draft", onSelect: openDraftPdf } : { label: pdfTab.busy != null ? MSG.pdfMaking : RFQ_MSG.pdfOpen, group: "doc" as const, disabled: pdfTab.busy != null, testId: "rfq-pdf", onSelect: () => void pdfTab.open(pdfUrl, { stored: true }) },
      ]
    : r.status === "draft"
      ? [
          // 送付：印刷・FAX とメールで送付状がオンのときは送付の画面（★16）、メールで送付状がオフなら短い確認の窓
          { label: RFQ_MSG.send, rank: 1, testId: "rfq-send", onSelect: () => (via === "print" || cover ? toSendPage(via, cover) : void gate("send").then((ok) => ok && setDlg("send"))) },
          { label: t(RFQ_MSG.copy), testId: "rfq-copy", onSelect: () => setDlg("copy") },
          { label: RFQ_MSG.pdfDraft, group: "doc", testId: "rfq-pdf-draft", onSelect: openDraftPdf },
          // ⋯ の「印刷・FAX で送付」：送付の画面（送付状は送信方法が印刷・FAX ならその設定、メールなら既定のオン＝印刷・FAX の既定）
          { label: RFQ_MSG.sendPrint, group: "doc", testId: "rfq-send-print", onSelect: () => toSendPage("print", via === "print" ? cover : true) },
          { label: RFQ_MSG.deleteDraft, group: "danger", danger: true, testId: "rfq-delete", onSelect: () => setDlg("delete") },
        ]
      : [
          ...(r.status === "sent" || r.status === "answered" ? [{ label: RFQ_MSG.registerAnswer, rank: 1, to: `/vendor-quotes/new?rfq=${r.id}`, testId: "rfq-register-answer" }] : []),
          ...(r.status === "sent" || r.status === "answered"
            ? [{ label: RFQ_MSG.answer, checked: r.status === "answered", testId: "rfq-answer", onSelect: () => void act(() => (r.status === "answered" ? api<Rfq>("DELETE", `/rfqs/${r.id}/answer`) : api<Rfq>("POST", `/rfqs/${r.id}/answer`, {})), r.status === "answered" ? RFQ_MSG.unanswered : RFQ_MSG.answered) }]
            : []),
          { label: RFQ_MSG.rerequest, testId: "rfq-rerequest", onSelect: () => void act(() => api<Rfq>("POST", `/rfqs/${r.id}/rerequest`), RFQ_MSG.rerequested) },
          { label: t(RFQ_MSG.copy), testId: "rfq-copy", onSelect: () => setDlg("copy") },
          { label: pdfTab.busy != null ? MSG.pdfMaking : RFQ_MSG.pdfOpen, group: "doc", disabled: pdfTab.busy != null, testId: "rfq-pdf", onSelect: () => void pdfTab.open(pdfUrl, { stored: true }) },
          // もう一度メールで送る：送付状がオンなら送付の画面（番号は採り直さない）、オフなら今の窓
          ...(r.status !== "cancelled" ? [{ label: RFQ_MSG.sendMail, group: "doc" as const, testId: "rfq-send-mail", onSelect: () => (via === "email" && cover ? toSendPage("email", true) : setDlg("resend")) }] : []),
          ...(r.status !== "cancelled" ? [{ label: RFQ_MSG.cancel, group: "danger" as const, danger: true, testId: "rfq-cancel", onSelect: () => setDlg("cancel") }] : []),
          // #275（docs/99 §5）：取消した依頼は「⋯」のいちばん下に「削除」（朱）＝完全に削除（番号は欠番）。送付済は先に取消
          ...(r.status === "cancelled" ? [{ label: RFQ_MSG.purge, group: "danger" as const, danger: true, testId: "rfq-purge", onSelect: () => setDlg("purge") }] : []),
        ];
  const saveText = sv.state.kind === "saving" ? RFQ_MSG.saving : sv.state.kind === "saved" ? RFQ_MSG.autoSaved.replace("{at}", sv.state.at) : sv.state.kind === "error" ? `⚠ ${sv.state.msg}` : "";
  // {vendor}は略称。同じ略称が2社あるときだけ「略称（正式名）」（#149）
  const vlist = [{ id: r.vendor.id, short: r.vendor.shortName, full: r.vendor.name }, ...(vendors.data?.items ?? []).filter((p) => p.id !== r.vendor.id).map((p) => ({ id: p.id, short: p.shortName || p.name, full: p.name }))];
  const vlabels = pickNamesOf(vlist);
  const vendorOpts = vlist.map((v, i) => ({ id: v.id, name: vlabels[i] ?? v.short }));

  // ---- 明細の表 ----
  const cols = rfqColumnsFor(sizer.widths);
  const ctx: TblCtx = { cols, showInternal: false, readOnly: !draft, alt: true, onCover: false, field: false, takeoff: false, taxCol: false, taxDateHint: false };
  const focusField = (lineId: string, f: FieldKey) => {
    setFocus({ id: lineId, f });
    const el = () => (f === "unit" ? document.getElementById(`u_${lineId}`) : document.querySelector<HTMLElement>(`[data-line="${lineId}"] [data-f="${f}"]`));
    // ある欄はすぐ（続けて打つ字を前の欄に入れない）、足したばかりの行は描いてから
    const now = el();
    if (now) now.focus();
    else window.requestAnimationFrame(() => el()?.focus());
  };
  const nextField = (lineId: string, f: FieldKey) => {
    const i = lines.findIndex((l) => l.id === lineId);
    const l = lines[i]!;
    const order = l.kind === "detail" ? FIELDS : (["name", "note"] as const);
    const k = (order as readonly FieldKey[]).indexOf(f);
    if (k >= 0 && k < order.length - 1) return focusField(lineId, order[k + 1]!);
    const nx = lines[i + 1];
    if (nx) return focusField(nx.id, "name");
    const add = blank();
    setRows([...lines, add], null, false);
    focusField(add.id, "name");
  };
  const editLine = (lineId: string, p: Partial<RfqLine>, key: string) => setRows(lines.map((l) => (l.id === lineId ? { ...l, ...p } : l)), `${lineId}:${key}`);
  const lineMenu = (l: RfqLine, i: number): RowMenuItem[] => [
    { label: RFQ_MSG.lineAddBelow, testId: "rfq-line-add-below", onSelect: () => { const add = blank(); setRows([...lines.slice(0, i + 1), add, ...lines.slice(i + 1)], null, true); focusField(add.id, "name"); } },
    { label: RFQ_MSG.lineUp, testId: "rfq-line-up", disabled: i === 0, onSelect: () => { const n = [...lines]; [n[i - 1], n[i]] = [n[i]!, n[i - 1]!]; setRows(n, null, true); } },
    { label: RFQ_MSG.lineDown, testId: "rfq-line-down", disabled: i === lines.length - 1, onSelect: () => { const n = [...lines]; [n[i + 1], n[i]] = [n[i]!, n[i + 1]!]; setRows(n, null, true); } },
    { label: RFQ_MSG.lineDelete, danger: true, divider: true, testId: "rfq-line-delete", onSelect: () => setRows(lines.filter((x) => x.id !== l.id), null, true) },
  ];
  const keyNext = (lineId: string, f: FieldKey) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing && !e.shiftKey) {
      e.preventDefault();
      nextField(lineId, f);
    }
  };
  const origin = (l: RfqLine) =>
    l.source === "estimate" ? (
      <span className="rqe-origin" title={t(RFQ_MSG.originTitle).replace("{no}", r.estimate?.no ?? RFQ_MSG.originEstimateOnly)} data-testid="rfq-origin">
        {l.estimateNo != null ? RFQ_MSG.originEstimate.replace("{n}", String(l.estimateNo)) : RFQ_MSG.originEstimateOnly}
      </span>
    ) : (
      <span className="chip rqe-own" data-testid="rfq-origin">{RFQ_MSG.originOwn}</span>
    );
  const rowsNode = lines.map((l, i) => {
    const on = (f: FieldKey) => focus?.id === l.id && focus.f === f;
    const inp = (f: FieldKey, node: ReactNode) => <span className={"rqe-cell" + (on(f) ? " is-on" : "")}>{node}</span>;
    const fprops = (f: FieldKey) => ({ "data-f": f, onFocus: () => setFocus({ id: l.id, f }), onBlur: () => undo.current.breakTyping() });
    const blankCells = (
      <>
        <td className="price r rqe-blank">—</td>
        <td className="amount r rqe-blank">—</td>
      </>
    );
    if (l.kind !== "detail") {
      return (
        <tr key={l.id} className={"line " + (l.kind === "heading" ? "group" : "note")} data-line={l.id} data-testid="rfq-line">
          <td className="no stk"><span className="nonum num">{i + 1}</span></td>
          <td className="name stk" colSpan={draft ? 6 : 6}>
            {draft ? (
              inp("name", <input className="ce" value={l.name} maxLength={RFQ_LIMITS.lineNameMax} aria-label={l.kind === "heading" ? RFQ_MSG.headingPlaceholder : t(RFQ_MSG.colName)} onChange={(e) => editLine(l.id, { name: e.target.value }, "name")} onKeyDown={keyNext(l.id, "name")} {...fprops("name")} />)
            ) : (
              <span className="nmt">{l.name}</span>
            )}
          </td>
          <td className="note">{draft ? inp("note", <input className="ce" value={l.note} maxLength={RFQ_LIMITS.lineNoteMax} aria-label={t(RFQ_MSG.colNote)} onChange={(e) => editLine(l.id, { note: e.target.value }, "note")} onKeyDown={keyNext(l.id, "note")} {...fprops("note")} />) : <span className="cut">{l.note}</span>}</td>
          <td className="origin">{origin(l)}</td>
          <td className="rqe-more">{draft ? <RowMenu label={RFQ_MSG.lineMenu.replace("{name}", l.name || "この行")} items={lineMenu(l, i)} testId="rfq-line-more" trigger={DOTS_H} triggerClass="rowmore dots" /> : null}</td>
        </tr>
      );
    }
    return (
      <tr key={l.id} className="line detail" data-line={l.id} data-testid="rfq-line">
        <td className="no stk"><span className="nonum num">{i + 1}</span></td>
        <td className="name stk">
          {draft ? inp("name", <input className="ce" value={l.name} maxLength={RFQ_LIMITS.lineNameMax} aria-label={t(RFQ_MSG.colName)} onChange={(e) => editLine(l.id, { name: e.target.value }, "name")} onKeyDown={keyNext(l.id, "name")} {...fprops("name")} data-testid="rfq-line-name" />) : <span className="nmt">{l.name}</span>}
          {l.place || l.trade ? <span className="rqe-axis muted">{[l.place, l.trade].filter(Boolean).join("・")}</span> : null}
        </td>
        <td className="spec">{draft ? inp("spec", <input className="ce" value={l.spec} maxLength={RFQ_LIMITS.lineSpecMax} aria-label={t(RFQ_MSG.colSpec)} onChange={(e) => editLine(l.id, { spec: e.target.value }, "spec")} onKeyDown={keyNext(l.id, "spec")} {...fprops("spec")} />) : <span className="cut">{l.spec}</span>}</td>
        <td className="qty r">
          {draft
            ? inp("qty", <NumInput kind="dec" className="ce r" value={l.quantity} aria-label={RFQ_MSG.colQty} onChange={(v) => editLine(l.id, { quantity: v }, "qty")} onKeyDown={keyNext(l.id, "qty")} {...fprops("qty")} />)
            : <span className="num">{l.quantity == null ? "" : fq(l.quantity)}</span>}
        </td>
        <td className="unit">
          {draft ? inp("unit", <UnitInput id={`u_${l.id}`} className="ce" value={l.unit} label={RFQ_MSG.colUnit} onChange={(v) => editLine(l.id, { unit: v }, "unit")} onKeyDown={keyNext(l.id, "unit")} testId="rfq-line-unit" />) : l.unit}
        </td>
        {blankCells}
        <td className="note">{draft ? inp("note", <input className="ce" value={l.note} maxLength={RFQ_LIMITS.lineNoteMax} aria-label={t(RFQ_MSG.colNote)} onChange={(e) => editLine(l.id, { note: e.target.value }, "note")} onKeyDown={keyNext(l.id, "note")} {...fprops("note")} />) : <span className="cut">{l.note}</span>}</td>
        <td className="origin">{origin(l)}</td>
        <td className="rqe-more">{draft ? <RowMenu label={RFQ_MSG.lineMenu.replace("{name}", l.name || "この行")} items={lineMenu(l, i)} testId="rfq-line-more" trigger={DOTS_H} triggerClass="rowmore dots" /> : null}</td>
      </tr>
    );
  });

  // ---- 添付の帯（②改2 10/20：1行 52px。右に送信設定の要約。要約を押すと帯の下に送信設定が1段広がる） ----
  const attachOn = can("receivedDocs");
  const over = r.attachBytes > RFQ_LIMITS.attachBytes;
  const kindOf = (a: Rfq["attachments"][number]) => (a.contentType === "application/pdf" ? RFQ_MSG.attachKindPdf : a.kind === "photo" ? RFQ_MSG.attachKindPhoto : RFQ_MSG.attachKindImage);
  /** 添付の帯の左：「添付 3」「2.0 ／ 10 MB」と、添付のチップ（種類・名前・×） */
  const attachHead = (
    <>
      <span className="rqe-bt">
        <b>{RFQ_MSG.attachTitle}</b>
        <span className="chip num" data-testid="rfq-attach-count">{r.attachments.length}</span>
        <small className={"num rqe-meter" + (over ? " shu" : "")} title={RFQ_MSG.attachTotal.replace("{meter}", rfqAttachMeter(r.attachBytes))} data-testid="rfq-attach-total">
          {rfqAttachMeter(r.attachBytes)}
        </small>
        <Help label={`${RFQ_MSG.attachTitle}の説明`} text={t(RFQ_MSG.attachHelp)} testId="help-rfq-attach" />
      </span>
      {r.attachments.length ? (
        <span className="rqe-atts" role="list" data-testid="rfq-attach-list">
          {r.attachments.map((a) => (
            <span key={a.id} role="listitem" className={"rqe-att" + (a.deleted ? " is-gone" : "")} title={`${a.sendName}　${a.sendVariant === "display" ? RFQ_MSG.attachDisplay : RFQ_MSG.attachOriginal.replace("{kind}", a.kind === "drawing" ? "図面" : "資料")}　${rfqSizeText(a.sendBytes)}`} data-testid="rfq-attach-item">
              <span className={"rqe-attk" + (a.contentType === "application/pdf" ? " is-pdf" : "")} aria-hidden="true">{kindOf(a)}</span>
              <span className="rqe-attt">{a.sendName}</span>
              {a.deleted ? <b className="shu rqe-attg">{RFQ_MSG.attachDeleted}</b> : null}
              {draft ? (
                <button type="button" className="rqe-attx" aria-label={RFQ_MSG.attachRemove.replace("{name}", a.sendName)} disabled={busy} onClick={() => void setAttach(r.attachments.filter((x) => x.id !== a.id).map((x) => x.documentId))} data-testid="rfq-attach-remove">
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
  const setSendOpen = (v: boolean) => {
    setSendOpenState(v);
    writeSendOpen(account, v);
  };
  /** 送信設定の要約：「メール・写真台帳・Bcc」 */
  const summary = [
    via === "email" ? RFQ_MSG.viaMail : RFQ_MSG.viaPrintText,
    r.show.photos && r.includePhotos && r.photos.total > 0 ? RFQ_MSG.ledgerTitle : null, // 写真の無い依頼には付かない
    r.show.takeoff && r.includeTakeoffs ? t(RFQ_MSG.includeTakeoffs) : null,
    via === "email" && bcc ? RFQ_MSG.summaryBcc : null,
    (draft || via === "email") && cover ? (coverWindow ? `${COVER_LETTER_MSG.coverSwitch}（${COVER_LETTER_MSG.windowSwitch}）` : COVER_LETTER_MSG.coverSwitch) : null,
  ]
    .filter(Boolean)
    .join("・");
  const summaryBtn = (
    <span className="rqe-sumwrap">
      <span className="rqe-vline" aria-hidden="true" />
      <button type="button" className="rqe-sum" aria-expanded={sendOpen} aria-controls="rqe-sendset" title={`${RFQ_MSG.sendSettings}　${summary}`} onClick={() => setSendOpen(!sendOpen)} data-testid="rfq-send-summary">
        <span className="rqe-suml">{RFQ_MSG.sendSettings}</span>
        <span className="rqe-sumv">{summary}</span>
        {/* #244（docs/94 B-5）：開閉の帯と同じ「開く ⌄／閉じる ⌃」（行は増やさない＝#220 の添付の帯 1行 52px のまま） */}
        <span className="fold-tg" aria-hidden="true">{sendOpen ? `${FOLD_MSG.close} ⌃` : `${FOLD_MSG.open} ⌄`}</span>
      </button>
    </span>
  );
  const pickBtn = (
    <button type="button" className="btn" disabled={busy} onClick={() => setDlg("attach")} data-testid="rfq-attach-pick">
      {RFQ_MSG.attachPick}
    </button>
  );
  const attachRow = (
    <div className="rqe-attrow" aria-label={RFQ_MSG.attachTitle} data-testid="rfq-attach">
      {attachOn && draft ? (
        <FileDrop
          onFiles={(fs) => void upload(fs)}
          accept={RECEIVED_ACCEPT}
          hint={attachHead}
          chooseLabel={RFQ_MSG.attachAdd}
          bare
          disabled={busy}
          testId="rfq-attach-drop"
          inputTestId="rfq-attach-file"
          before={
            <>
              {summaryBtn}
              {pickBtn}
            </>
          }
        />
      ) : (
        <div className="rqe-attstatic">
          <span className="rqe-atthint">{attachHead}</span>
          {summaryBtn}
        </div>
      )}
    </div>
  );
  // ---- 送信設定（開いた形）：送信先／送信方法／添付／印字項目／次回も同じ設定・閉じる ----
  /** いまの送信設定（「次回も同じ設定」で覚える形） */
  const prefsNow = (over: Partial<RfqSendPrefs> = {}): RfqSendPrefs => ({
    via,
    bcc,
    includePhotos: r.includePhotos,
    photosPerPage: r.photosPerPage,
    includeTakeoffs: r.includeTakeoffs,
    printAnswerBox: r.printAnswerBox,
    printTerms: r.printTerms,
    cover,
    coverWindow,
    ...over,
  });
  /** 送信設定を変える：下書きに保存する欄は patch、「次回も同じ設定」がオンなら覚えた値も変える */
  const setSend = (over0: Partial<RfqSendPrefs>) => {
    const over = { ...over0 };
    // 送信方法を変えたら送付状は既定に（印刷・FAX＝オン・メール＝オフ）。送付状をオフにしたら窓付封筒もオフ（★8）
    if (over.via !== undefined && over.via !== via && over.cover === undefined) {
      over.cover = over.via === "print";
      over.coverWindow = false;
    }
    if (over.cover === false) over.coverWindow = false;
    if (over.via !== undefined) setVia(over.via);
    if (over.bcc !== undefined) setBcc(over.bcc);
    if (over.cover !== undefined) setCover(over.cover);
    if (over.coverWindow !== undefined) setCoverWindow(over.coverWindow);
    const { via: _v, bcc: _b, cover: _c, coverWindow: _w, ...saved } = over;
    if (Object.keys(saved).length) {
      // スイッチは押した瞬間に変わって見える（答えを待たない。答えが来たら入れ替わる）
      qc.setQueryData<Rfq>(KEY(r.id), (old) => (old ? { ...old, ...saved } : old));
      patch(saved, true);
    }
    if (remember) writeSendPrefs(account, prefsNow(over));
  };
  const sw = (label: string, checked: boolean, onChange: (v: boolean) => void, testId: string, disabled = !draft) => (
    <span className="rqe-bsw">
      {/* ②改2：つまみ＋名前（オン／オフの文字は出さない＝横に並ぶ小さなスイッチ。状態はつまみの位置と色の両方で分かる） */}
      <Switch checked={checked} onChange={onChange} label={label} disabled={disabled} testId={testId} bare>
        <span aria-hidden="true">{label}</span>
      </Switch>
    </span>
  );
  const contactText = r.contact ? `${r.vendor.shortName}　${[r.contact.department, r.contact.name].filter(Boolean).join(" ")} 様` : `${r.vendor.shortName} 御中`;
  const attachCol = r.show.photos || r.show.takeoff || (draft && via === "email");
  const sendSet = sendOpen ? (
    <div className="rqe-sendset" id="rqe-sendset" role="group" aria-label={RFQ_MSG.sendSettings} data-testid="rfq-sendcard">
      <div className="rqe-sc is-to">
        <span className="rqe-scl">{RFQ_MSG.sendTo}</span>
        {draft ? (
          <select value={r.contact?.id ?? ""} aria-label={RFQ_MSG.sendTo} onChange={(e) => patch({ contactId: e.target.value || null }, true)} data-testid="rfq-contact">
            <option value="">{`${r.vendor.shortName} 御中`}</option>
            {r.contacts.map((c) => (
              <option key={c.id} value={c.id}>{`${r.vendor.shortName}　${[c.department, c.name].filter(Boolean).join(" ")} 様`}</option>
            ))}
          </select>
        ) : (
          <b data-testid="rfq-contact-text">{contactText}</b>
        )}
        {r.contact?.email ? <span className="muted rqe-mail" data-testid="rfq-contact-email">{r.contact.email}</span> : null}
      </div>
      <div className="rqe-sc is-via">
        <span className="rqe-scl">{RFQ_MSG.sendVia}</span>
        <span className="rqe-via" role="group" aria-label={RFQ_MSG.sendVia}>
          {(["email", "print"] as const).map((k) => (
            <button key={k} type="button" className={"chip" + (via === k ? " is-on" : "")} aria-pressed={via === k} disabled={!draft} onClick={() => setSend({ via: k })} data-testid={`rfq-via-${k}`}>
              {k === "email" ? RFQ_MSG.viaMail : RFQ_MSG.viaPrint}
            </button>
          ))}
        </span>
        {draft && via === "print" && r.attachments.length ? <span className="hint rqe-pnote">{RFQ_MSG.attachPrintNote.replace("{n}", String(r.attachments.length))}</span> : null}
        {/* #223（docs/89 §5.2）：送付状は送信方法と組（印刷・FAX＝既定オン・メール＝既定オフ）。窓付封筒は送付状がオンのときだけ出す（★8）。横に1行＝送信設定の段の高さを変えない */}
        {draft || via === "email" ? (
          <span className="rqe-swrow">
            {sw(COVER_LETTER_MSG.coverSwitch, cover, (v) => setSend({ cover: v }), "rfq-cover", false)}
            {cover ? sw(COVER_LETTER_MSG.windowSwitch, coverWindow, (v) => setSend({ coverWindow: v }), "rfq-cover-window", false) : null}
            {cover ? <Help label={COVER_LETTER_MSG.windowSwitch} text={COVER_LETTER_MSG.windowHelp} testId="help-rfq-cover-window" /> : null}
          </span>
        ) : null}
      </div>
      {attachCol ? (
        <div className="rqe-sc">
          <span className="rqe-scl">{RFQ_MSG.attachTitle}</span>
          {r.show.photos ? (
            <span className="rqe-swrow">
              {sw(RFQ_MSG.includePhotos, r.includePhotos, (v) => setSend({ includePhotos: v }), "rfq-photos")}
              {r.includePhotos ? (
                <span className="seg rqe-seg" role="group" aria-label={RFQ_MSG.ledgerTitle}>
                  {([3, 6] as const).map((n) => (
                    <button key={n} type="button" aria-pressed={r.photosPerPage === n} disabled={!draft} onClick={() => setSend({ photosPerPage: n })} data-testid={`rfq-per-${n}`}>
                      {n === 3 ? RFQ_MSG.perPage3 : RFQ_MSG.perPage6}
                    </button>
                  ))}
                </span>
              ) : null}
            </span>
          ) : null}
          {r.show.takeoff ? sw(t(RFQ_MSG.includeTakeoffs), r.includeTakeoffs, (v) => setSend({ includeTakeoffs: v }), "rfq-takeoffs") : null}
          {draft && via === "email" ? sw(RFQ_MSG.bccSelf, bcc, (v) => setSend({ bcc: v }), "rfq-bcc-switch", false) : null}
        </div>
      ) : null}
      <div className="rqe-sc">
        <span className="rqe-scl">{RFQ_MSG.printGroup}</span>
        {sw(RFQ_MSG.printAnswerBox, r.printAnswerBox, (v) => setSend({ printAnswerBox: v }), "rfq-print-answer")}
        {sw(RFQ_MSG.printTerms, r.printTerms, (v) => setSend({ printTerms: v }), "rfq-print-terms")}
      </div>
      <div className="rqe-sc is-end">
        {draft
          ? sw(
              RFQ_MSG.rememberSettings,
              remember,
              (v) => {
                setRemember(v);
                writeSendPrefs(account, v ? prefsNow() : null);
              },
              "rfq-remember",
              false,
            )
          : null}
        <button type="button" className="btn rqe-scclose" onClick={() => setSendOpen(false)} data-testid="rfq-sendset-close">
          閉じる
        </button>
      </div>
    </div>
  ) : null;
  const band = (
    <section className={"rqe-band" + (sendOpen ? " is-open" : "")} data-testid="rfq-band">
      {attachRow}
      {upErr ? <ErrorText>{upErr}</ErrorText> : null}
      {over ? <b className="shu rqe-over">{RFQ_MSG.attachOver.replace("{size}", rfqSizeText(r.attachBytes)).replace("{limit}", rfqSizeText(RFQ_LIMITS.attachBytes))}</b> : null}
      {sendSet}
    </section>
  );

  const log = !r.canEdit ? (
    <section className="rqe-card rqe-log" data-testid="rfq-log">
      <div className="rqe-cardh"><h2>{RFQ_MSG.cardHistory}</h2></div>
      {r.cancelReason ? <p className="muted">{`取消の理由：${r.cancelReason}`}</p> : null}
      <ul>
        {r.sentAt ? <li>{`${mdTime(r.sentAt)}　${r.sentVia === "email" ? RFQ_MSG.sentByMail : RFQ_MSG.sentByPrint}　${r.no ?? ""}`}</li> : null}
        {r.deliveries.map((d, i) => (
          <li key={i} data-testid="rfq-delivery">{`${mdTime(d.at)}　${RFQ_MSG.sentByMail}　${d.to}${d.bccSelf ? "（控えあり）" : ""}　${d.by}`}</li>
        ))}
        {r.responses.map((x, i) => (
          <li key={"r" + i} data-testid="rfq-response">
            {`${mdTime(x.at)}　`}
            {x.vendorQuoteId ? <Link to={`/vendor-quotes/${x.vendorQuoteId}`}>{t(RFQ_MSG.responseVendorQuote)}</Link> : RFQ_MSG.responseManual}
          </li>
        ))}
      </ul>
    </section>
  ) : null;

  const estLabel = r.estimate ? (r.estimate.no ? `${RFQ_MSG.estimateLink} ${r.estimate.no}` : `${RFQ_MSG.estimateLink}${RFQ_MSG.draftNo}`) : "";

  return (
    <div className={"rqe" + (narrow ? " is-phone" : "")} data-testid="rfq-page">
      {/* #245（docs/94 B-4 案B）：頭は DocHead の3段。2段目＝見積依頼書の絵と名前・件名（その場で修正）・No.・状態。3段目＝{vendor}・依頼日・回答期限・担当・元（見積・{order}）｜詳細 */}
      <DocHead
        back={back}
        save={
          <span className="autosave dh-save2 vql-saved rqe-saved" aria-live="polite" data-testid="rfq-saved">
            <span>{sv.state.kind === "saving" ? RFQ_MSG.saving : sv.state.kind === "error" ? `⚠ ${sv.state.msg}` : "自動保存"}</span>{" "}
            <span className="num">{sv.state.kind === "saved" ? sv.state.at : "\u00a0"}</span>
          </span>
        }
        undo={
          draft ? (
            <span className="undo rqe-undo" role="group" aria-label="元に戻す・やり直す">
              <button type="button" className="undobtn rqe-ubtn" aria-label="元に戻す" title="元に戻す" disabled={!undo.current.canUndo} onClick={() => undoDo(false)} data-testid="rfq-undo">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" /></svg>
              </button>
              <button type="button" className="undobtn rqe-ubtn" aria-label="やり直す" title="やり直す" disabled={!undo.current.canRedo} onClick={() => undoDo(true)} data-testid="rfq-redo">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" /></svg>
              </button>
            </span>
          ) : null
        }
        primary={<span className="hd-acts" data-testid="head-actions"><HeadActions actions={actions} label={RFQ_MSG.title} primary part="main" /></span>}
        menu={<HeadActions actions={actions} label={RFQ_MSG.title} part="more" />}
        kind="rfqs"
        kindLabel={RFQ_MSG.title}
        title={<input className="dh-subj" style={subjWidth(head.subject)} aria-label={RFQ_MSG.subjectLabel} placeholder={RFQ_MSG.subjectLabel} value={head.subject} maxLength={RFQ_LIMITS.subjectMax} readOnly={!draft} onChange={(e) => setH({ subject: e.target.value })} data-testid="rfq-subject" />}
        chips={
          <>
            {/* 送ったあとは番号。下書きは札「下書き」だけ（題の後ろに「（下書き）」を書かない＝2026-10-21 ★17） */}
            {r.no ? <span className="chip num rqe-no" data-testid="rfq-no">{`No. ${r.no}`}</span> : null}
            <span className="chip" data-status={r.status} data-testid="rfq-status">{RFQ_STATUS_LABEL[r.status]}{r.status === "sent" && r.sentAt ? ` ${md(r.sentAt.slice(0, 10))}` : ""}</span>
            {r.supersededBy ? <span className="chip rqe-tag">{RFQ_MSG.supersededBy.replace("{no}", r.supersededBy)}</span> : null}
            {r.status === "sent" && !r.supersededBy && r.overdueDays ? <span className="chip shu rqe-tag" data-testid="rfq-overdue">{overdueText(r)}</span> : null}
          </>
        }
        total={null}
        meta={
          <>
            {draft ? (
              <DocField label={<label htmlFor="rfq-vendor-sel">{L.vendor}</label>}>
                <select id="rfq-vendor-sel" className="inp" value={r.vendor.id} onChange={(e) => patch({ vendorId: e.target.value }, true)} data-testid="rfq-vendor">
                  {vendorOpts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </DocField>
            ) : (
              <DocField label={L.vendor}>
                <b title={r.vendor.name} data-testid="rfq-vendor-text">{r.vendor.shortName}</b>
              </DocField>
            )}
            <DocField label={RFQ_MSG.requestDate}>
              <DateInput inline value={head.requestDate ?? ""} label={RFQ_MSG.requestDate} ariaLabel={RFQ_MSG.requestDate} readOnly={!draft} onChange={(v) => setH({ requestDate: v || null }, true)} testId="rfq-request-date" />
            </DocField>
            <DocField label={<>{RFQ_MSG.replyBy}<Help label={`${RFQ_MSG.replyBy}の説明`} text={RFQ_MSG.replyByHelp} testId="help-rfq-reply" /></>}>
              <DateInput inline value={head.replyBy ?? ""} label={RFQ_MSG.replyBy} ariaLabel={RFQ_MSG.replyBy} readOnly={!draft} onChange={(v) => setH({ replyBy: v || null }, true)} testId="rfq-reply" />
            </DocField>
            {draft ? (
              <DocField label={<label htmlFor="rfq-staff-sel">{RFQ_MSG.staff}</label>}>
                <select id="rfq-staff-sel" className="inp" value={r.staff?.id ?? ""} onChange={(e) => patch({ staffId: e.target.value || null }, true)} data-testid="rfq-staff">
                  <option value="">{RFQ_MSG.staffNone}</option>
                  {r.staffOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </DocField>
            ) : (
              <DocField label={RFQ_MSG.staff}>
                <b>{r.staff?.name ?? "—"}</b>
              </DocField>
            )}
            {r.project ? (
              <DocField label={L.project}>
                <span className="rqe-proj" title={r.project.name} data-testid="rfq-project">{r.project.name}</span>
              </DocField>
            ) : null}
            {r.estimate || r.orders.length ? (
              <DocField label="元">
                <span className="row" style={{ gap: 12 }}>
                  {r.estimate ? (
                    <Link className="rqe-est" to={`/estimates/${r.estimate.id}`} title={`${r.estimate.no ?? RFQ_MSG.draftNo}　${r.estimate.subject}`} data-testid="rfq-estimate-link">
                      {estLabel} ›
                    </Link>
                  ) : null}
                  {/* #233 2a：この依頼（と回答）から作った{order}「{order} PO-0001 ›」 */}
                  {r.orders.map((o) => (
                    <Link key={o.id} className="rqe-est" to={`/orders/${o.id}`} data-testid="rfq-order-link">
                      {`${t("{order}")} ${o.no ?? ORDER_MSG.chipDraft}`} ›
                    </Link>
                  ))}
                </span>
              </DocField>
            ) : null}
            {/* #223 の2回目：送付の記録（送付状を付けて印刷・保存・メールした回数。0回なら出さない） */}
            {!draft ? <DeliveriesLink filter={{ rfq: r.id }} testId="rfq-deliveries" /> : null}
          </>
        }
        tools={
          <button type="button" className="btn sm quiet detailbtn rqe-details" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="rfq-details">
            {RFQ_MSG.details} <span className={"vqd-chev" + (open ? " is-open" : "")} aria-hidden="true" />
          </button>
        }
        testId="rfq-head"
      >
        {open ? (
          <div className="vqd-r3" data-testid="rfq-details-body">
            <label className="vql-fld rqe-f is-site">
              <span>{L.site}</span>
              <input value={head.siteAddress} maxLength={RFQ_LIMITS.siteMax} readOnly={!draft} onChange={(e) => setH({ siteAddress: e.target.value })} data-testid="rfq-site" />
            </label>
            <label className="vql-fld rqe-f is-period">
              <span>{L.period}</span>
              <input value={head.workPeriodText} maxLength={RFQ_LIMITS.periodMax} readOnly={!draft} onChange={(e) => setH({ workPeriodText: e.target.value })} data-testid="rfq-period" />
            </label>
            {r.legalDays && r.legalDays > 1 ? <p className="hint" data-testid="rfq-legal">{RFQ_MSG.legalDays.replace("{days}", String(r.legalDays))}</p> : null}
          </div>
        ) : null}
      </DocHead>

      {band}

      <section className="rqe-main" aria-label={RFQ_MSG.colLines}>
        {draft ? (
          <div className="rqe-tools" data-testid="rfq-tools">
            <button type="button" className="btn" disabled={busy || !r.project} onClick={() => setDlg("import")} data-testid="rfq-import-open">{RFQ_MSG.importLines}</button>
            <button type="button" className="btn" onClick={() => { const add = blank(); setRows([...lines, add], null, true); focusField(add.id, "name"); }} data-testid="rfq-add-line">{RFQ_MSG.addLine}</button>
            <button type="button" className="btn text" onClick={() => { const add = blank("heading"); setRows([...lines, add], null, true); focusField(add.id, "name"); }} data-testid="rfq-add-heading">{RFQ_MSG.addHeading}</button>
            <span className="sp" />
            <span className="muted num rqe-small" data-testid="rfq-line-summary">{RFQ_MSG.lineSummary.replace("{n}", String(lines.length)).replace("{a}", String(counts.est)).replace("{b}", String(counts.own))}</span>
          </div>
        ) : null}
        {lines.length ? (
          <EstimateTable ctx={ctx} customer={L.customer} foot={null} testId="rfq-table" sizer={narrow ? undefined : sizer}>
            {rowsNode}
          </EstimateTable>
        ) : (
          <p className="hint rqe-empty" data-testid="rfq-empty">{RFQ_MSG.linesEmpty}</p>
        )}
        {/* 条件・依頼文（紙の「備考」）：カードのいちばん下に1行。押すと数行に広がる（大きな空の枠を置かない） */}
        <div className={"rqe-termsrow" + (termsOpen ? " is-open" : "")} data-testid="rfq-terms-row">
          <label htmlFor="rq_terms" className="rqe-termsl">{RFQ_MSG.termsBox}</label>
          <textarea
            id="rq_terms"
            className="rqe-termsv"
            rows={termsOpen ? 4 : 1}
            wrap={termsOpen ? "soft" : "off"}
            value={head.terms}
            maxLength={RFQ_LIMITS.termsMax}
            readOnly={!draft}
            onFocus={() => draft && setTermsOpen(true)}
            onChange={(e) => setH({ terms: e.target.value })}
            data-testid="rfq-terms"
          />
          <button type="button" className="btn text rqe-termsbtn" aria-expanded={termsOpen} aria-controls="rq_terms" onClick={() => setTermsOpen(!termsOpen)} data-testid="rfq-terms-toggle">
            {termsOpen ? RFQ_MSG.termsCollapse : RFQ_MSG.termsExpand} <span className={"vqd-chev" + (termsOpen ? " is-open" : "")} aria-hidden="true" />
          </button>
        </div>
      </section>
      {log}

      {back.dialog}
      {sv.dialog}
      {dlg === "send" || dlg === "resend" ? (
        <SendDialog
          rfq={r}
          bcc={bcc}
          title={dlg === "resend" ? RFQ_MSG.sendMailDo : RFQ_MSG.sendTitle}
          busy={busy}
          onClose={() => setDlg(null)}
          onSent={(v) => {
            put(v);
            refreshAround();
            toast(RFQ_MSG.sent.replace("{no}", v.no ?? ""));
            setDlg(null);
          }}
          beforeSend={async () => {
            const why = await sv.ready();
            return why == null ? null : saveGateText("send", why);
          }}
        />
      ) : null}
      {dlg === "cancel" ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label={RFQ_MSG.cancel} onKeyDown={(e) => e.key === "Escape" && setDlg(null)}>
            <div className="dh">{RFQ_MSG.cancel}</div>
            <div className="db">
              <p style={{ marginTop: 0 }}>{RFQ_MSG.cancelAsk}</p>
              <Field id="rq_reason" label={RFQ_MSG.cancelReason}>
                <input id="rq_reason" className="inp" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-testid="rfq-cancel-reason" />
              </Field>
            </div>
            <div className="df">
              <button type="button" className="btn" onClick={() => setDlg(null)}>閉じる</button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void act(() => api<Rfq>("POST", `/rfqs/${r.id}/cancel`, { reason }), RFQ_MSG.cancelled)} data-testid="rfq-cancel-confirm">{RFQ_MSG.cancel}</button>
            </div>
          </div>
        </div>
      ) : null}
      {dlg === "delete" ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label="削除" onKeyDown={(e) => e.key === "Escape" && setDlg(null)}>
            <div className="dh">削除</div>
            <div className="db"><p style={{ margin: 0 }}>{RFQ_MSG.deleteAsk}</p></div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setDlg(null)}>取消</button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void doDelete()} data-testid="rfq-delete-confirm">削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {dlg === "purge" ? <RfqPurgeDialog rfq={r} to={r.project ? `/groups/${r.project.id}?tab=vendor` : "/rfqs"} onClose={() => setDlg(null)} /> : null}
      {dlg === "copy" ? <CreateRfqDialog copyOf={r} onClose={() => setDlg(null)} /> : null}
      {dlg === "import" ? (
        <ImportDialog
          rfq={r}
          afterLineId={focus?.id ?? null}
          onClose={() => setDlg(null)}
          onImport={async (estimateId, lineageIds) => {
            if (!(await sv.settle())) return;
            setBusy(true);
            try {
              const v = await api<Rfq>("POST", `/rfqs/${r.id}/import`, { version: version.current, estimateId, lineageIds, afterLineId: focus?.id ?? null });
              // 取込は ↶ で戻せる（控えてから答えで入れ替え・履歴は切らない＝#195 の復元と同じ作り）
              undo.current.record(linesRef.current);
              put(v);
              linesRef.current = v.lines;
              setLines(v.lines);
              bump((x) => x + 1);
              refreshAround();
              toast(RFQ_MSG.importDone.replace("{n}", String(lineageIds.length)));
              setDlg(null);
            } catch (e) {
              toast(messageOf(e), undefined, "error");
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
      {dlg === "attach" && r.project ? (
        <AttachPickDialog groupId={r.project.id} chosen={r.attachments.map((a) => a.documentId)} onClose={() => setDlg(null)} onPick={(ids) => void setAttach(ids)} />
      ) : null}
    </div>
  );
}

/** 窓「見積の明細から取込」（docs/88 §4.3）：見積を選ぶ（依頼に見積が付いたあとはその見積）→ 行を選ぶ（この{vendor}の行がはじめから選ばれる）→「取込」 */
function ImportDialog({ rfq, onClose, onImport, afterLineId }: { rfq: Rfq; afterLineId: string | null; onClose: () => void; onImport: (estimateId: string, lineageIds: string[]) => Promise<void> }) {
  const L = useLabels();
  const [est, setEst] = useState<string | null>(null);
  const w = useQuery({ queryKey: ["rfq-estimate-lines", rfq.id, est], queryFn: () => api<RfqEstimateLines>("GET", `/rfqs/${rfq.id}/estimate-lines${est ? `?estimateId=${est}` : ""}`) });
  const data = w.data;
  const all = (data?.sheets ?? []).flatMap((s) => s.lines);
  const [sel, setSel] = useState<Set<string> | null>(null);
  // この{vendor}の付いた行をはじめから選ぶ（取込済の行は選ばない）
  useEffect(() => {
    if (data) setSel(new Set(all.filter((l) => l.vendor?.id === rfq.vendor.id && !l.imported).map((l) => l.lineageId)));
  }, [data?.estimateId]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = sel ?? new Set<string>();
  const allOn = all.length > 0 && picked.size === all.length;
  const [busy, setBusy] = useState(false);
  void afterLineId;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg wide rqe-imp" role="dialog" aria-label={RFQ_MSG.importTitle} onKeyDown={(e) => e.key === "Escape" && !busy && onClose()} data-testid="rfq-import-dialog">
        <div className="dh">{RFQ_MSG.importTitle}</div>
        <div className="db">
          <div className="rqe-imphead">
            {data?.fixed ? (
              <span data-testid="rfq-import-estimate">{`${RFQ_MSG.importEstimate}　${data.estimates.find((e) => e.id === data.estimateId)?.no ?? "（下書き）"}　${data.estimates.find((e) => e.id === data.estimateId)?.subject ?? ""}`}</span>
            ) : (
              <label className="vql-fld">
                <span>{RFQ_MSG.importEstimate}</span>
                <select value={data?.estimateId ?? ""} disabled={!data} onChange={(e) => setEst(e.target.value)} data-testid="rfq-import-estimate-pick">
                  {(data?.estimates ?? []).map((e) => <option key={e.id} value={e.id}>{`${e.no ?? "（下書き）"}　${e.subject}`}</option>)}
                </select>
              </label>
            )}
            <span className="sp" />
            {all.length ? (
              <button type="button" className="btn text sm" onClick={() => setSel(allOn ? new Set() : new Set(all.map((l) => l.lineageId)))} data-testid="rfq-import-all">
                {allOn ? RFQ_MSG.pickNone : RFQ_MSG.pickAll}
              </button>
            ) : null}
          </div>
          {data && !all.length ? <p className="hint" data-testid="rfq-import-none">{RFQ_MSG.importNone}</p> : null}
          <div className="rqe-implist" aria-busy={!data || undefined}>
            {(data?.sheets ?? []).map((s, si) => (
              <table key={si} className="list rqe-imptbl">
                {s.name ? <caption>{s.name}</caption> : null}
                <thead>
                  <tr>
                    <th aria-label="選択" />
                    <th>No</th>
                    <th>{t(RFQ_MSG.colName)}</th>
                    <th>{t(RFQ_MSG.colSpec)}</th>
                    <th className="r">{RFQ_MSG.colQty}</th>
                    <th>{RFQ_MSG.colUnit}</th>
                    <th>{L.vendor}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.lines.map((l) => (
                    <tr key={l.lineageId} className={l.kind === "heading" ? "is-heading" : undefined} data-testid="rfq-import-line">
                      <td>
                        <input
                          type="checkbox"
                          checked={picked.has(l.lineageId)}
                          aria-label={l.name || t("（{lineName}なし）")}
                          onChange={(e) => {
                            const n = new Set(picked);
                            if (e.target.checked) n.add(l.lineageId);
                            else n.delete(l.lineageId);
                            setSel(n);
                          }}
                          data-testid="rfq-import-check"
                        />
                      </td>
                      <td className="num">{l.no ?? ""}</td>
                      <td>
                        {l.kind === "heading" ? <b>{l.name}</b> : l.name}
                        {l.imported ? <span className="chip rqe-imported">{RFQ_MSG.importedTag}</span> : null}
                      </td>
                      <td>{l.spec}</td>
                      <td className="r num">{l.quantity == null ? "" : fq(l.quantity)}</td>
                      <td>{l.unit}</td>
                      <td className="muted">{l.vendor?.shortName ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </div>
        <div className="df">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !picked.size || !data?.estimateId}
            onClick={() => {
              setBusy(true);
              void onImport(data!.estimateId!, all.filter((l) => picked.has(l.lineageId)).map((l) => l.lineageId)).finally(() => setBusy(false));
            }}
            data-testid="rfq-import-do"
          >
            {RFQ_MSG.importDo}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 窓「受け取った書類から選択」（docs/88 §4.4）：その{project}の受け取った書類（削除していない・文字だけでない）を 図面 → 写真 → 資料 → ほか の順。□ で選ぶ →「決定」 */
function AttachPickDialog({ groupId, chosen, onClose, onPick }: { groupId: string; chosen: string[]; onClose: () => void; onPick: (ids: string[]) => void }) {
  const q = useQuery({ queryKey: ["received", groupId], queryFn: () => api<ReceivedList>("GET", `/groups/${groupId}/received-documents`) });
  const rank: Record<string, number> = { drawing: 0, photo: 1, material: 2 };
  const docs = (q.data?.items ?? []).filter((d) => d.format !== "text").sort((a, b) => (rank[a.kind] ?? 3) - (rank[b.kind] ?? 3));
  const [sel, setSel] = useState<string[]>(chosen);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg rqe-pick" role="dialog" aria-label={RFQ_MSG.attachPickTitle} onKeyDown={(e) => e.key === "Escape" && onClose()} data-testid="rfq-attach-dialog">
        <div className="dh">{RFQ_MSG.attachPickTitle}</div>
        <div className="db">
          {q.data && !docs.length ? <p className="hint">{RFQ_MSG.attachPickEmpty}</p> : null}
          <ul className="rqe-picklist" aria-busy={!q.data || undefined}>
            {docs.map((d) => (
              <li key={d.id}>
                <label className="chk">
                  <input
                    type="checkbox"
                    checked={sel.includes(d.id)}
                    onChange={(e) => setSel(e.target.checked ? [...sel, d.id] : sel.filter((x) => x !== d.id))}
                    data-testid="rfq-attach-check"
                  />
                  <span>
                    {d.title}
                    <small className="muted">{`　${d.kind === "drawing" ? "図面" : d.kind === "photo" ? "写真" : "資料"}${d.pageCount ? `　P.${d.pageCount}` : ""}　${rfqSizeText(d.bytes)}`}</small>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onClose}>取消</button>
          <button
            type="button"
            className="btn primary"
            disabled={sel.length > RFQ_LIMITS.attachments}
            onClick={() => {
              onPick(sel);
              onClose();
            }}
            data-testid="rfq-attach-pick-do"
          >
            {RFQ_MSG.attachPickDo}
          </button>
        </div>
      </div>
    </div>
  );
}

export const isRfqError = (e: unknown, code: string) => e instanceof ApiError && e.code === code;
